import React, { useState } from 'react'
import { Row } from 'reactstrap'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { alertError, alertSuccess } from 'utils/notifications'
import API from 'utils/API'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons'

interface FedExShipmentButtonProps {
  data: any
  setData?: (callback: (prevData: any[]) => any[]) => void
  filteredRows?: any[]
  onBeforeCreate?: (createFn: () => Promise<void>) => void
}

const FedExShipmentButton = (props: FedExShipmentButtonProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [currentTrackingUrl, setCurrentTrackingUrl] = useState<string | null>(props.data.tracking_url)
  const { formatMessage } = useIntl()
  const f = (id: any) => formatMessage({ id })

  // Synchroniser le state local avec props.data.tracking_url
  React.useEffect(() => {
    setCurrentTrackingUrl(props.data.tracking_url)
  }, [props.data.tracking_url])

  // Déterminer l'état du bouton en fonction de tracking_url
  const getButtonState = (): 'create' | 'cancel' | 'none' => {
    const trackingUrl = currentTrackingUrl

    // Si tracking_url est vide, null ou undefined -> bouton de création
    if (!trackingUrl || trackingUrl === '' || trackingUrl === null) {
      return 'create'
    }

    // Si tracking_url commence par l'URL FedEx -> bouton d'annulation
    if (typeof trackingUrl === 'string' && trackingUrl.startsWith('https://www.fedex.com')) {
      return 'cancel'
    }

    // Sinon, pas de bouton (autre transporteur)
    return 'none'
  }

  const handleCreateShipment = async (): Promise<void> => {
    if (isLoading) return

    // Si onBeforeCreate est fourni, on déclenche la validation d'adresse d'abord
    if (props.onBeforeCreate) {
      props.onBeforeCreate(createShipmentInternal)
      return
    }

    // Sinon, création directe
    await createShipmentInternal()
  }

  const createShipmentInternal = async (): Promise<void> => {
    if (isLoading) return

    setIsLoading(true)

    try {
      const response = await API.post('/pharmacist/fedex/process-order', {
        orderId: props.data.orderId,
        options: {
          validate: true,
          skipValidationErrors: false,
          updateDatabase: true
        }
      })

      if (response.data.success === true || response.status === 200) {
        alertSuccess(f('fedexShipmentCreatedSuccess'))

        // Récupérer tous les champs de la réponse API pour mise à jour complète
        const apiData = response.data.data
        const trackingUrl = apiData?.trackingUrl
        const trackingCode = apiData?.trackingNumber || apiData?.updatedFields?.tracking_code
        const newState = apiData?.updatedFields?.state

        // Mise à jour immédiate du state local pour réactivité instantanée
        setCurrentTrackingUrl(trackingUrl)

        // Mise à jour locale complète de tous les champs retournés par l'API
        if (props.setData && props.filteredRows) {
          props.setData((prevData: any[]) =>
            prevData.map((order: any) =>
              (order.orderId === props.data.orderId && order.pharmacyOrderId === props.data.pharmacyOrderId)
                ? {
                    ...order,
                    tracking_url: trackingUrl,
                    tracking_code: trackingCode,
                    ...(newState !== undefined && { state: newState })
                  }
                : order
            )
          )
        }
      } else {
        alertError(f('fedexShipmentCreateError'))
      }
    } catch (error: any) {
      console.error('Error creating FedEx shipment:', error)
      const errorMessage = error.response?.data?.message || f('fedexShipmentCreateError')
      alertError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelShipment = async (): Promise<void> => {
    if (isLoading) return

    setIsLoading(true)

    try {
      const response = await API.post('/pharmacist/fedex/cancel-shipment', {
        orderId: props.data.orderId,
        options: {
          updateDatabase: true
        }
      })

      if (response.data.success === true || response.status === 200) {
        alertSuccess(f('fedexShipmentCanceledSuccess'))

        // Récupérer les champs mis à jour de la réponse API (si fournis)
        const apiData = response.data.data
        const updatedState = apiData?.updatedFields?.state

        // Mise à jour immédiate du state local pour réactivité instantanée
        setCurrentTrackingUrl(null)

        // Mise à jour locale complète pour vider les champs tracking
        if (props.setData && props.filteredRows) {
          props.setData((prevData: any[]) =>
            prevData.map((order: any) =>
              (order.orderId === props.data.orderId && order.pharmacyOrderId === props.data.pharmacyOrderId)
                ? {
                    ...order,
                    tracking_url: null,
                    tracking_code: null,
                    ...(updatedState !== undefined && { state: updatedState })
                  }
                : order
            )
          )
        }
      } else {
        alertError(f('fedexShipmentCancelError'))
      }
    } catch (error: any) {
      console.error('Error canceling FedEx shipment:', error)
      const errorMessage = error.response?.data?.message || f('fedexShipmentCancelError')
      alertError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const buttonState = getButtonState()
  // Commande Phacil non payée : le backend refuse la création (PAYMENT_PENDING)
  const paymentPending = props.data.paymentPending === true

  // Ne rien afficher si le bouton ne doit pas être visible
  if (buttonState === 'none') {
    return null
  }

  return (
    <StyledRow>
      {buttonState === 'create' && (
        <CreateButton
          onClick={handleCreateShipment}
          disabled={isLoading || paymentPending}
          title={paymentPending ? f('fedexShipmentPaymentPending') : undefined}
        >
          {isLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span style={{ marginLeft: '8px' }}>{f('fedexShipmentCreating')}</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faTruck} />
              <span style={{ marginLeft: '8px' }}>{f(paymentPending ? 'fedexShipmentPaymentPending' : 'fedexShipmentCreate')}</span>
            </>
          )}
        </CreateButton>
      )}

      {buttonState === 'cancel' && (
        <CancelButton
          onClick={handleCancelShipment}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span style={{ marginLeft: '8px' }}>{f('fedexShipmentCanceling')}</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faTimes} />
              <span style={{ marginLeft: '8px' }}>{f('fedexShipmentCancel')}</span>
            </>
          )}
        </CancelButton>
      )}
    </StyledRow>
  )
}

const StyledRow = styled(Row)`
  position: relative;
  -webkit-flex: 1 1 auto;
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
  padding: 0.5rem;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  margin-left: 9px;
`

const BaseButton = styled.button`
  border-radius: 5px;
  padding: 10px 15px;
  width: 15rem;
  min-height: 3rem;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const CreateButton = styled(BaseButton)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5568d3 0%, #63408a 100%);
  }
`

const CancelButton = styled(BaseButton)`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border: none;
  color: white;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #d87ee5 0%, #e0475a 100%);
  }
`

export default FedExShipmentButton
